import React, { useState } from "react";

import {
  mdiDownload,
  mdiFilmstrip,
  mdiMicrophone,
  mdiPencil,
  mdiSubtitlesOutline,
  mdiTelevisionBox,
  mdiVideoMarkerOutline,
} from "@mdi/js";
import Icon from "@mdi/react";
import classnames from "classnames";
import { useTranslation } from "react-i18next";

import CardDeleteButton from "./CardDeleteButton";
import CardLanguageSelect from "./CardLanguageSelect";
import CardQualityBadge from "./CardQualityBadge";
import CardVttBadge from "./CardVttBadge";
import StatusBadge from "./StatusBadge";

import type {
  EncodingStream,
  StreamType,
  UpdateStreamData,
} from "../../../api/EncoderAPI.types";

export const iconByStreamType: Record<
  Exclude<StreamType, "thumbnails">,
  string
> = {
  video: mdiFilmstrip,
  audio: mdiMicrophone,
  subtitles: mdiSubtitlesOutline,
  fast: mdiTelevisionBox,
  d2g: mdiDownload,
  chapters: mdiVideoMarkerOutline,
};

function StreamEncodingProgressBar({ stream }: { stream: EncodingStream }) {
  if (stream.status !== "transcoding") return null;
  return (
    <div className="bg-status-transcoding/10 absolute -inset-x-1 bottom-0 z-10 h-1.5 overflow-hidden rounded-lg">
      <div
        className="bg-status-transcoding h-full animate-pulse transition-all duration-1000"
        style={{ width: `${stream.progression ?? 0}%` }}
      />
    </div>
  );
}

interface CardOttAssetProps {
  stream: EncodingStream;
  onDelete: () => void;
  onUpdateStream: (streamId: string, data: UpdateStreamData) => void;
}

export default function CardOttAsset({
  stream,
  onDelete,
  onUpdateStream,
}: CardOttAssetProps) {
  const { t } = useTranslation("editor-encoding");
  const [isEditing, setIsEditing] = useState(false);
  const [draftName, setDraftName] = useState(stream.name);

  function handleRenameSubmit(): void {
    if (draftName !== stream.name) {
      onUpdateStream(stream.uuid, { name: draftName });
    }
    setIsEditing(false);
  }

  function handleRenameClick(): void {
    setDraftName(stream.name);
    setIsEditing(true);
  }

  function handleRenameKeyDown(
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void {
    if (event.key === "Enter") handleRenameSubmit();
    if (event.key === "Escape") {
      setDraftName(stream.name);
      setIsEditing(false);
    }
  }

  const canDelete = stream.status === "transcoded" || stream.status === "error";
  const typeBadgeClassNames = classnames(
    "self-center rounded-lg px-1.25 py-0.75 text-[0.78125rem] font-semibold",
    "bg-primary/10 text-primary",
  );

  const streamQualityLabel = (() => {
    if (stream.type === "video") return `${stream.manifest.quality}p`;
    if (stream.type === "audio") return `${stream.manifest.quality}kbps`;
    if (stream.type === "d2g") return t("d2g");
    if (stream.type === "subtitles") return t("subtitles");
    if (stream.type === "chapters") return t("chapters");
    return "";
  })();

  if (stream.type === "thumbnails") return null;

  return (
    <div className="group border-border bg-card relative flex items-start gap-4 overflow-hidden rounded-lg border p-3">
      <StreamEncodingProgressBar stream={stream} />
      <div className={typeBadgeClassNames}>
        <Icon path={iconByStreamType[stream.type]} size={1.25} />
      </div>
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex min-h-7 min-w-0 items-center pb-1">
            {isEditing ? (
              <input
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                className="border-primary bg-card flex w-full flex-1 items-center rounded border px-1.5 py-px text-sm outline-none"
                onBlur={handleRenameSubmit}
                onChange={(e): void => setDraftName(e.target.value)}
                onKeyDown={handleRenameKeyDown}
                value={draftName}
              />
            ) : (
              <div
                className="flex min-w-0 flex-1 items-center gap-2"
                onClick={handleRenameClick}
              >
                <p className="truncate text-sm">{stream.name}</p>
                <button
                  className="text-muted-foreground hover:text-muted-foreground invisible shrink-0 group-hover:visible"
                  type="button"
                >
                  <Icon path={mdiPencil} size={0.625} />
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {stream.type === "subtitles" || stream.type === "chapters" ? (
              <CardVttBadge
                onTypeChange={(type) => onUpdateStream(stream.uuid, { type })}
                type={stream.type}
              />
            ) : (
              <CardQualityBadge qualityLabel={streamQualityLabel} />
            )}
            {(stream.type === "audio" ||
              stream.type === "subtitles" ||
              stream.type === "chapters") && (
              <CardLanguageSelect
                language={stream.language_uuid}
                onLanguageChange={(language_uuid) =>
                  onUpdateStream(stream.uuid, { language_uuid })
                }
              />
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <StatusBadge
            progression={stream.progression}
            status={stream.status}
          />
          {canDelete ? (
            <CardDeleteButton
              cancelLabel={t("cancel")}
              confirmLabel={t("deleteStreamConfirm")}
              message={t("deleteStreamMessage", { name: stream.name })}
              onDelete={onDelete}
              title={t("deleteStreamTitle")}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
